import { Component, HostBinding, Input, OnInit, Output, EventEmitter, inject, ChangeDetectorRef, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormsModule, NgControl } from '@angular/forms';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { Moment } from 'moment';
import { TranslatePipe } from '@ngx-translate/core';

type InputType = 'text' | 'password' | 'textarea' | 'select' | 'datepicker';
export interface SelectOption {
  label: string;
  value: string | number;
}

@Component({
  selector: 'ui-form-field',
  imports: [FormsModule, SvgIconComponent, MatDatepickerModule, TranslatePipe],
  templateUrl: './form-field.component.html',
  styleUrls: ['./form-field.component.scss'],
})
export class FormFieldComponent implements OnInit, ControlValueAccessor {
  ngControl = inject(NgControl, { optional: true, self: true });
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  @Input() label = '';
  @Input() placeholder = '';
  @Input() type: InputType = 'text';
  @Input() suffixIcon = '';
  @Input() errorMessages: Record<string, string> = {};
  @Input() fullWidth = false;

  // Event emitters
  @Output() focus = new EventEmitter<void>();
  @Output() blur = new EventEmitter<void>();
  @Output() suffixIconClick = new EventEmitter<void>();
  // Select Inputs
  @Input() options: SelectOption[] = [];

  // Datepicker
  @Input() minDate: Moment | null = null;

  value: string | number | null = '';
  isDisabled = false;
  touched = false;
  control: any;

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.control = this.ngControl?.control;
    // showError reads the control, which a parent can mark touched (e.g. on submit).
    this.control?.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cdr.markForCheck());
  }

  // Conditionally apply the class on the host element
  @HostBinding('class.full-width') get isFullWidth() {
    return this.fullWidth;
  }

  onChange = (_value: string | number | null) => {};
  onTouched = () => {};

  writeValue(value: string): void {
    this.value = value;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cdr.markForCheck();
  }

  onDateChange(event: any): void {
    const date = event.value;
    this.value = date ? date.format('YYYY-MM-DD') : '';
    this.onChange(this.value);
  }

  onFocus(): void {
    this.focus.emit();
  }

  onBlur(): void {
    this.onTouched();
    this.touched = true;
    this.blur.emit();
  }

  clearDatePicker(event: MouseEvent) {
    event.stopPropagation();
    this.value = null;
    this.onChange(this.value);
  }

  get showError(): boolean {
    const ctrl = this.control;
    return !!ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched);
  }

  get errors(): string[] {
    const errors = this.control?.errors ?? {};
    return Object.keys(errors).map((key) => this.errorMessages[key] || key);
  }
}
